import dayjs from '@/lib/dayjs'
import type { SalesOrderPaymentHistory } from '@/lib/services/sales-orders/sales-orders.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'

type HistoryEntry = SalesOrderPaymentHistory['entries'][number]
/** A payment taken, a refund given back, or a refund canceled: the receipts the order page lists. */
type ReceiptEntry = Extract<HistoryEntry, { kind: 'payment' | 'refund' | 'refund_reversal' }>
type ReceiptKind = ReceiptEntry['kind']
/**
 * A change to the order's total, listed among the receipts where it happened. The order's total already includes
 * it, so it is a note: the receipts alone take the total to the balance.
 */
type ChangeEntry = Extract<HistoryEntry, { kind: 'order_change' }>

export type SalesOrderPaymentTone = 'success' | 'warning' | 'destructive' | 'neutral'

export type SalesOrderPaymentLine = {
  id: string
  kind: ReceiptKind | ChangeEntry['kind']
  label: string
  /** Signed: payments come off the total, refunds go back to the customer. Null for an order change, which the total already counts. */
  amount: number | null
  detail: string | null
}

export type SalesOrderPaymentBalance = {
  label: string
  amount: number
  tone: SalesOrderPaymentTone
}

export type SalesOrderPaymentLedger = {
  lines: SalesOrderPaymentLine[]
  status: { tone: SalesOrderPaymentTone; headline: string; detail: string | null }
  balance: SalesOrderPaymentBalance
}

type LedgerOptions = {
  total: number
  termsLabel?: string | null
  dueDateLabel?: string | null
}

const LINE_LABELS: Record<ReceiptKind, (entry: ReceiptEntry) => string> = {
  payment: (entry) => `Paid · ${entry.method_label}`,
  refund: () => 'Refunded',
  refund_reversal: () => 'Refund canceled',
}

// Payments come off the total; refunds go back to the customer; a reversal brings it back.
const LINE_SIGNS: Record<ReceiptKind, number> = { payment: -1, refund: 1, refund_reversal: -1 }

// "Cash" and "Check 2087" read as "to cash" and "to check 2087"; brands such as VISA keep
// their capitals.
function formatDestination(label: string): string {
  return /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label
}

// What a change added to or took off the order's total, which the API signs.
function formatChange(amount: number): string {
  const change = parseCurrency(amount)

  return `${change < 0 ? 'Took off' : 'Added'} ${formatCurrency(Math.abs(change))}`
}

function formatLineTime(createdAt: string | null): string | null {
  if (!createdAt) return null
  const at = dayjs(createdAt)
  const isThisYear = at.format('YYYY') === dayjs().format('YYYY')

  return at.format(isThisYear ? 'MMM D, h:mm A' : 'MMM D, YYYY, h:mm A')
}

/**
 * Presentation for the order page's payment status and receipt lines. The API owns the
 * history and the status (`payment_history`, read from dated payment postings); this only
 * words it. Returns null when the order response has no payment history.
 */
export function getSalesOrderPaymentLedger(
  order: { payment_history?: SalesOrderPaymentHistory | null } | null | undefined,
  { total, termsLabel, dueDateLabel }: LedgerOptions
): SalesOrderPaymentLedger | null {
  const history = order?.payment_history
  if (!history?.state) return null

  const { state } = history
  const lines: SalesOrderPaymentLine[] = (history.entries ?? []).map((entry) =>
    entry.kind === 'order_change'
      ? {
          id: entry.id,
          kind: entry.kind,
          label: 'Order changed',
          amount: null,
          detail: [formatChange(entry.amount), formatLineTime(entry.created_at)]
            .filter(Boolean)
            .join(' · '),
        }
      : {
          id: entry.id,
          kind: entry.kind,
          label: LINE_LABELS[entry.kind](entry),
          amount: LINE_SIGNS[entry.kind] * Number(entry.amount),
          detail:
            [
              entry.kind === 'payment' ? null : `To ${formatDestination(entry.method_label)}`,
              formatLineTime(entry.created_at),
              entry.reason,
            ]
              .filter(Boolean)
              .join(' · ') || null,
        }
  )
  const collected = Number(state.collected_total)
  const refunded = Number(state.refunded_total)
  const balanceDue = Number(state.balance_due_total)
  const firstPayment = (history.entries ?? []).find((entry) => entry.kind === 'payment')
  const howPaid =
    state.payment_count > 1
      ? `Split across ${state.payment_count} payments`
      : (firstPayment?.method_label ?? null)
  const balanceDueLine: SalesOrderPaymentBalance = {
    label: 'Balance due',
    amount: balanceDue,
    tone: 'warning',
  }

  switch (state.status) {
    case 'cancelled':
      return {
        lines,
        status: {
          tone: 'neutral',
          headline: 'Cancelled',
          detail:
            refunded > 0
              ? `${formatCurrency(refunded)} refunded · nothing owed`
              : collected > 0
                ? `Collected ${formatCurrency(collected)}`
                : `Total ${formatCurrency(total)} · nothing was collected`,
        },
        balance:
          balanceDue > 0
            ? balanceDueLine
            : { label: 'Cancelled · nothing owed', amount: 0, tone: 'neutral' },
      }
    case 'balance_due': {
      const dueText =
        termsLabel && dueDateLabel && dueDateLabel !== 'Due on receipt'
          ? `${termsLabel} · due ${dueDateLabel}`
          : 'Due on receipt'

      return {
        lines,
        status: {
          tone: 'warning',
          headline: `Balance due ${formatCurrency(balanceDue)}`,
          detail: `Collected ${formatCurrency(parseCurrency(state.kept_total))} · ${dueText}`,
        },
        balance: balanceDueLine,
      }
    }
    case 'refunded':
      return {
        lines,
        status: {
          tone: 'destructive',
          headline: `Refunded ${formatCurrency(refunded)}`,
          detail: `Total ${formatCurrency(total)} · nothing owed`,
        },
        balance: { label: 'Refunded · nothing owed', amount: 0, tone: 'neutral' },
      }
    case 'partly_refunded':
      return {
        lines,
        status: {
          tone: 'neutral',
          headline: 'Partly refunded',
          detail: `Collected ${formatCurrency(collected)} · ${formatCurrency(refunded)} refunded`,
        },
        balance: { label: 'Nothing owed', amount: 0, tone: 'neutral' },
      }
    case 'paid':
      return {
        lines,
        status: {
          tone: 'success',
          headline: 'Paid in full',
          detail: [`Collected ${formatCurrency(collected)}`, howPaid].filter(Boolean).join(' · '),
        },
        balance: { label: 'Paid in full', amount: 0, tone: 'success' },
      }
    case 'nothing_owed':
    default:
      return {
        lines,
        status: { tone: 'neutral', headline: 'Nothing owed', detail: null },
        balance: { label: 'Nothing owed', amount: 0, tone: 'neutral' },
      }
  }
}
